/**
 * WebhooksPage — Full webhook management (inbound + outbound)
 *
 * Features:
 *   - Inbound webhook config (auth webhook status + secret rotation)
 *   - Outbound webhook CRUD (create, edit, delete endpoints)
 *   - Event category subscriptions per endpoint
 *   - Delivery history and sync status
 *
 * @module packages/billing-admin/src/pages/WebhooksPage
 * @since 2026-06-08
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InboundConfig {
  endpoint: string;
  secret_masked: string;
  is_active: boolean;
  last_received_at: string | null;
  total_received: number;
  total_failed: number;
}

interface OutboundEndpoint {
  id: string;
  url: string;
  event_types: string[];
  is_active: boolean;
  secret_masked: string;
  created_at: string;
  last_delivery_at?: string | null;
}

interface WebhookDelivery {
  id: string;
  event_type: string;
  event_id: string;
  status: string;
  created_at: string;
  customer_name?: string;
}

interface SyncStatus {
  last_sync_at: string | null;
  total_customers: number;
  total_subscriptions: number;
  sync_healthy: boolean;
}

// Available event categories for outbound webhooks
const EVENT_CATEGORIES = [
  { category: 'customer', label: 'Customer Events', events: ['customer.created', 'customer.updated', 'customer.deleted'] },
  { category: 'subscription', label: 'Subscription Events', events: ['subscription.created', 'subscription.updated', 'subscription.cancelled', 'subscription.expired'] },
  { category: 'credits', label: 'Credit Events', events: ['credits.granted', 'credits.deducted', 'credits.low_balance', 'credits.exhausted'] },
  { category: 'usage', label: 'Usage Events', events: ['usage.threshold_reached', 'usage.budget_exceeded', 'usage.anomaly_detected'] },
  { category: 'invoice', label: 'Invoice Events', events: ['invoice.created', 'invoice.paid', 'invoice.overdue', 'invoice.voided'] },
  { category: 'plan', label: 'Plan & Permission Events', events: ['plan.updated', 'plan.permissions_changed', 'plan.deprecated'] },
];

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useInboundConfig() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<InboundConfig>({
    queryKey: ['webhooks', 'inbound-config'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function useOutboundEndpoints() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<OutboundEndpoint[]>({
    queryKey: ['webhooks', 'outbound-endpoints'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/endpoints`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function useCreateEndpoint() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { url: string; event_types: string[] }) => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/endpoints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Create failed: ${res.status}`);
      return (await res.json()).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks', 'outbound-endpoints'] }),
  });
}

function useUpdateEndpoint() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; url?: string; event_types?: string[]; is_active?: boolean }) => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/endpoints/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      return (await res.json()).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks', 'outbound-endpoints'] }),
  });
}

function useDeleteEndpoint() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/endpoints/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks', 'outbound-endpoints'] }),
  });
}

function useTestEndpoint() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/endpoints/${id}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Test failed: ${res.status}`);
      return (await res.json()).data;
    },
  });
}

function useDeliveries() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<WebhookDelivery[]>({
    queryKey: ['webhooks', 'deliveries'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/deliveries?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function useSyncStatus() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<SyncStatus>({
    queryKey: ['webhooks', 'sync-status'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/sync-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function useTriggerSync() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/trigger-sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

function useRotateInboundSecret() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (secret: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/webhooks/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ secret }),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks', 'inbound-config'] }),
  });
}

// ---------------------------------------------------------------------------
// Create Endpoint Modal
// ---------------------------------------------------------------------------

function CreateEndpointModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const createEndpoint = useCreateEndpoint();

  function toggleEvent(event: string): void {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  }

  function toggleCategory(events: string[]): void {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      const allSelected = events.every((e) => next.has(e));
      if (allSelected) events.forEach((e) => next.delete(e));
      else events.forEach((e) => next.add(e));
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!url.trim() || selectedEvents.size === 0) return;
    createEndpoint.mutate(
      { url: url.trim(), event_types: [...selectedEvents] },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl max-h-[85vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-slate-900">Create Outbound Webhook</h2>
        <p className="mt-1 text-sm text-slate-500">
          Configure an endpoint to receive billing events. A signing secret will be generated automatically.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* URL */}
          <div>
            <label className="text-sm font-medium text-slate-700">Endpoint URL</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required
              placeholder="https://your-app.com/webhooks/billing"
              className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-2.5 font-mono text-sm" />
          </div>

          {/* Event subscriptions */}
          <div>
            <label className="text-sm font-medium text-slate-700">Subscribe to Events</label>
            <p className="mt-1 text-xs text-slate-500">Select which event types this endpoint should receive.</p>
            <div className="mt-3 space-y-4">
              {EVENT_CATEGORIES.map((cat) => {
                const allSelected = cat.events.every((e) => selectedEvents.has(e));
                const someSelected = cat.events.some((e) => selectedEvents.has(e));
                return (
                  <div key={cat.category} className="rounded-lg border border-slate-200 p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={() => toggleCategory(cat.events)}
                        className="h-4 w-4 rounded border-slate-300 text-cyan-600" />
                      <span className="text-sm font-medium text-slate-900">{cat.label}</span>
                    </label>
                    <div className="mt-2 ml-6 flex flex-wrap gap-2">
                      {cat.events.map((event) => (
                        <label key={event} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={selectedEvents.has(event)} onChange={() => toggleEvent(event)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-cyan-600" />
                          <span className="font-mono text-xs text-slate-600">{event}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={createEndpoint.isPending || !url.trim() || selectedEvents.size === 0}
              className="flex-1 rounded-lg bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800 disabled:opacity-50">
              {createEndpoint.isPending ? 'Creating...' : 'Create Endpoint'}
            </button>
            <button type="button" onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
          </div>
          {createEndpoint.isError && <p className="text-sm text-red-600">{(createEndpoint.error as Error).message}</p>}
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WebhooksPage(): React.JSX.Element {
  const [tab, setTab] = useState<'outbound' | 'inbound' | 'deliveries'>('outbound');
  const [showCreate, setShowCreate] = useState(false);
  const [showSecretForm, setShowSecretForm] = useState(false);
  const [newSecret, setNewSecret] = useState('');

  const inboundQuery = useInboundConfig();
  const outboundQuery = useOutboundEndpoints();
  const deliveriesQuery = useDeliveries();
  const syncQuery = useSyncStatus();
  const triggerSync = useTriggerSync();
  const rotateSecret = useRotateInboundSecret();
  const updateEndpoint = useUpdateEndpoint();
  const deleteEndpoint = useDeleteEndpoint();
  const testEndpoint = useTestEndpoint();

  const config = inboundQuery.data;
  const endpoints = outboundQuery.data ?? [];
  const deliveries = deliveriesQuery.data ?? [];
  const sync = syncQuery.data;

  function handleSecretSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (newSecret.trim()) {
      rotateSecret.mutate(newSecret.trim(), { onSuccess: () => { setShowSecretForm(false); setNewSecret(''); } });
    }
  }

  return (
    <AdminLayout title="Webhooks & Sync">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Webhooks & Integrations</h1>
            <p className="mt-1 text-sm text-slate-500">
              Configure how rule42-billing communicates with your applications. Create outbound webhooks, manage inbound receivers, and monitor delivery.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="rounded-xl bg-cyan-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-cyan-800">
            + Create Webhook
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {(['outbound', 'inbound', 'deliveries'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
              {t === 'outbound' ? 'Outbound Endpoints' : t === 'inbound' ? 'Inbound Receivers' : 'Delivery History'}
            </button>
          ))}
        </div>

        {/* ====== OUTBOUND TAB ====== */}
        {tab === 'outbound' && (
          <div className="space-y-4">
            {outboundQuery.isLoading && <p className="text-sm text-slate-400">Loading endpoints...</p>}
            {endpoints.length === 0 && !outboundQuery.isLoading && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white/50 p-12 text-center">
                <p className="text-sm text-slate-500">No outbound webhooks configured yet.</p>
                <p className="mt-1 text-xs text-slate-400">Create an endpoint to start receiving billing events in your applications.</p>
                <button onClick={() => setShowCreate(true)}
                  className="mt-4 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800">
                  Create First Webhook
                </button>
              </div>
            )}
            {endpoints.map((ep) => (
              <div key={ep.id} className="rounded-2xl border border-white/70 bg-white/90 p-5 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-2 w-2 rounded-full ${ep.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                      <p className="font-mono text-sm text-slate-900 truncate">{ep.url}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {ep.event_types.slice(0, 5).map((evt) => (
                        <span key={evt} className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{evt}</span>
                      ))}
                      {ep.event_types.length > 5 && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">+{ep.event_types.length - 5} more</span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-400">
                      Created {new Date(ep.created_at).toLocaleDateString()}
                      {ep.last_delivery_at && ` · Last delivery ${new Date(ep.last_delivery_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => testEndpoint.mutate(ep.id)}
                      className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                      Test
                    </button>
                    <button onClick={() => updateEndpoint.mutate({ id: ep.id, is_active: !ep.is_active })}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium ${ep.is_active ? 'border-amber-200 text-amber-700 hover:bg-amber-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
                      {ep.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => { if (confirm('Delete this webhook endpoint?')) deleteEndpoint.mutate(ep.id); }}
                      className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ====== INBOUND TAB ====== */}
        {tab === 'inbound' && (
          <div className="space-y-6">
            {/* Auth Inbound Webhook */}
            <div className="rounded-2xl border border-white/70 bg-white/90 p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Auth Service Webhook</p>
                  <p className="mt-1 text-xs text-slate-500">Receives customer and subscription events from your identity provider</p>
                </div>
                {config && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {config.is_active ? 'Active' : 'Not Configured'}
                  </span>
                )}
              </div>

              {inboundQuery.isLoading && <p className="mt-4 text-sm text-slate-400">Loading...</p>}
              {config && (
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium text-slate-500">Endpoint</p>
                    <p className="mt-1 font-mono text-sm text-slate-900">{config.endpoint}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Shared Secret</p>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="font-mono text-sm text-slate-900">{config.secret_masked}</p>
                      <button onClick={() => setShowSecretForm(!showSecretForm)}
                        className="text-xs text-cyan-700 hover:text-cyan-800 font-medium">
                        {showSecretForm ? 'Cancel' : 'Rotate'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Last Received</p>
                    <p className="mt-1 text-sm text-slate-900">{config.last_received_at ? new Date(config.last_received_at).toLocaleString() : 'Never'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Stats</p>
                    <p className="mt-1 text-sm text-slate-900">{config.total_received} received · {config.total_failed} failed</p>
                  </div>
                </div>
              )}

              {showSecretForm && (
                <form onSubmit={handleSecretSubmit} className="mt-4 flex items-end gap-3 border-t border-slate-100 pt-4">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600">New Shared Secret</label>
                    <input type="password" value={newSecret} onChange={(e) => setNewSecret(e.target.value)}
                      placeholder="Enter new HMAC secret..."
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm" />
                  </div>
                  <button type="submit" disabled={rotateSecret.isPending || !newSecret.trim()}
                    className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800 disabled:opacity-50">
                    {rotateSecret.isPending ? 'Saving...' : 'Save'}
                  </button>
                </form>
              )}

              {/* Required events */}
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Accepted Event Types</p>
                <div className="flex flex-wrap gap-1.5">
                  {['subscription.created', 'subscription.updated', 'member.added', 'member.removed', 'org.deleted'].map((evt) => (
                    <span key={evt} className="rounded-full bg-cyan-50 border border-cyan-200 px-2.5 py-0.5 font-mono text-[10px] text-cyan-700">{evt}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sync Status */}
            <div className="rounded-2xl border border-white/70 bg-white/90 p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Sync Status</p>
                <button onClick={() => triggerSync.mutate()} disabled={triggerSync.isPending}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50 disabled:opacity-50">
                  {triggerSync.isPending ? 'Syncing...' : 'Trigger Full Sync'}
                </button>
              </div>
              {sync && (
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500">Health</p>
                    <span className={`mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${sync.sync_healthy ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {sync.sync_healthy ? 'Healthy' : 'Unhealthy'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Last Sync</p>
                    <p className="mt-1 text-sm text-slate-900">{sync.last_sync_at ? new Date(sync.last_sync_at).toLocaleString() : 'Never'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Customers</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{sync.total_customers}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">Subscriptions</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{sync.total_subscriptions}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== DELIVERIES TAB ====== */}
        {tab === 'deliveries' && (
          <div className="rounded-2xl border border-white/70 bg-white/90 p-6 shadow-lg">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700 mb-4">Recent Deliveries</p>
            {deliveriesQuery.isLoading && <p className="text-sm text-slate-400">Loading...</p>}
            {deliveries.length === 0 && !deliveriesQuery.isLoading && (
              <p className="text-sm text-slate-400">No deliveries recorded yet</p>
            )}
            {deliveries.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="pb-3 pr-4">Event Type</th>
                    <th className="pb-3 pr-4">Event ID</th>
                    <th className="pb-3 pr-4">Customer</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id} className="border-b border-slate-100">
                      <td className="py-2.5 pr-4 font-mono text-xs text-slate-700">{d.event_type}</td>
                      <td className="py-2.5 pr-4 font-mono text-[11px] text-slate-400">{d.event_id?.slice(0, 12) ?? '—'}...</td>
                      <td className="py-2.5 pr-4 text-slate-700">{d.customer_name || '—'}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          d.status === 'ok' || d.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' :
                          d.status === 'already_processed' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>{d.status}</span>
                      </td>
                      <td className="py-2.5 text-xs text-slate-500">{new Date(d.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && <CreateEndpointModal onClose={() => setShowCreate(false)} />}
      </div>
    </AdminLayout>
  );
}

